import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgClass } from '@angular/common';
import { userRegisterModel } from '@shared/model/customerModels/register-view-model';
import { debounceTime, Subscription, tap } from 'rxjs';
import type { IGoogleAuthResponse } from '@app/auth/models/google-auth-response.model';
import { AuthViewEnum } from '../../enums/auth-step-enum';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { EGender } from '@shared/enums/gender.enum';
import { SharedModule } from '@shared/module/shared.module';
import { LocalRepository } from '@shared/helper/local-repository';
import { ButtonLoaderDirective } from '@shared/directive/button-loader/button-loader.directive';

@Component({
  selector: 'app-register-form-presenter',
  imports: [
    SharedModule,
    ReactiveFormsModule,
    NgClass,
    ButtonLoaderDirective,
    MatDatepickerModule,
  ],
  templateUrl: './register-form-presenter.component.html',
  styleUrl: './register-form-presenter.component.scss'
})
export class RegisterFormPresenterComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private _localRepository = inject(LocalRepository);


  @Input() registerationLoading = false;
  @Input() verificationCode = 0;
  @Input() googleData!: IGoogleAuthResponse;
  @Input() isUsernameDuplicated = false;
  @Input() usernameCheckLoading = false;
  @Input() registerStep: RegisterStepEnum = RegisterStepEnum.ProfileCompletion
  @Output() onUserNameChanged = new EventEmitter<string>();
  @Output() onUsernameCheckLoading = new EventEmitter<null>();
  @Output() onOtpDesired = new EventEmitter<AuthViewEnum>();
  @Output() onRegisterSubmited = new EventEmitter<userRegisterModel>();
  @Output() onRegisterStep = new EventEmitter<RegisterStepEnum>()
  form!: FormGroup;
  valueChangeSub = new Subscription;

  minDate = new Date(new Date().getFullYear() - 100, 0, 1);
  maxDate = new Date();
  genderControl = new FormControl(null);
  registerStepEnum = RegisterStepEnum;
  EGender = EGender;


  ngOnInit(): void {
    this.initForm();
    this.handleUsernameCheck();
    if (this.googleData)
      this.form.get('userName')?.setValue(this.googleData.email.split('@')[0]);
  }

  initForm() {

    let defaultName = '';
    if (this.googleData?.firstName && this.googleData?.lastName)
      defaultName = this.googleData?.firstName + ' ' + this.googleData?.lastName;
    else if (this.googleData?.firstName)
      defaultName = this.googleData?.firstName;
    else if (this.googleData?.lastName)
      defaultName = this.googleData?.lastName;
    else
      defaultName = '';

    this.form = this.fb.group({
      fullName: new FormControl(defaultName, Validators.required),
      userName: new FormControl(null, [Validators.required, Validators.minLength(3), Validators.pattern(/^[_A-Za-z]+([a-zA-Z0-9_.])*$/)]),
      birthDay: new FormControl()
    });
  }

  get isButtonDisabled(): boolean {
    if (this.registerStep === RegisterStepEnum.ProfileCompletion) {
      return this.form.get('fullName')?.invalid ?? false;
    } else if (this.registerStep === RegisterStepEnum.UsernameSetup) {
      const isInvalid = this.form.get('userName')?.invalid ?? false;
      return isInvalid || this.isUsernameDuplicated || this.usernameCheckLoading;
    }
    return false;
  }

  submitForm() {
    const data: userRegisterModel = new userRegisterModel();
    data.user = {
      firstname: this.form.get('fullName')?.value ?? '',
      username: this.form.get('userName')?.value,
    };
    if (this.googleData) {
      data.code = this.googleData.idToken;
    }
    else {
      data.code = this.verificationCode.toString();
    }
    this.onRegisterSubmited.emit(data);
  }

  handleUsernameCheck() {
    const usernameControl = this.form.get('userName')!;
    this.valueChangeSub = usernameControl.valueChanges.pipe(
      tap(() => this.onUsernameCheckLoading.emit()),
      debounceTime(300),
      tap(v => {
        if (usernameControl.valid)
          this.onUserNameChanged.emit(v);
        else
          this.isUsernameDuplicated = false;
      })
    )
      .subscribe();
  }

  loginWithOtp() {
    this.onOtpDesired.emit(AuthViewEnum.userName);
  }

  continue() {
    if (this.registerStep == RegisterStepEnum.ProfileCompletion) {
      this.registerStep = RegisterStepEnum.UsernameSetup
    } else {
      this.submitForm()
    }
    this.onRegisterStep.emit(this.registerStep)
  }

  onGenderChange(gender: EGender) {
    this._localRepository.setLocalItem('selectedGender', gender.toString());
  }

  ngOnDestroy(): void {
    this.valueChangeSub.unsubscribe();
  }

}

export enum RegisterStepEnum {
  ProfileCompletion,
  UsernameSetup
}

